import { cn } from '../../lib/utils';

export type KartiMood = 'neutral' | 'happy' | 'sad' | 'sleep' | 'wow' | 'nag';

const INK = '#161512';
const PAPER = '#FFFCF5';

/** Arm angle per mood (0 = straight out, positive = raised, negative = hanging). */
const ARM: Record<KartiMood, number> = { neutral: -28, happy: 55, sad: -62, sleep: -40, wow: 75, nag: -8 };

function Arm({ side, angle, mood }: { side: 'l' | 'r'; angle: number; mood: KartiMood }) {
  const x = side === 'l' ? 8 : 82;
  const flip = side === 'l' ? 1 : -1;
  return (
    <g transform={`translate(${x} 62) scale(${flip} 1) rotate(${angle})`}>
      <rect
        x={-17}
        y={-3.8}
        width={18}
        height={7.6}
        rx={3.8}
        fill={PAPER}
        stroke={INK}
        strokeWidth={2.6}
        className={mood === 'neutral' ? 'karti-arm' : undefined}
      />
    </g>
  );
}

/**
 * Karti mascot: a paper flashcard with a face on a yellow back card.
 * One fixed look, six moods. Animations are plain CSS (see index.css) and
 * switched off under prefers-reduced-motion.
 */
export function Karti({
  size = 96,
  mood: moodProp = 'neutral',
  animated = true,
  className,
}: {
  size?: number;
  /** Legacy names 'sleepy' and 'cheer' are still accepted. */
  mood?: KartiMood | 'sleepy' | 'cheer';
  animated?: boolean;
  className?: string;
}) {
  const mood: KartiMood = moodProp === 'sleepy' ? 'sleep' : moodProp === 'cheer' ? 'happy' : moodProp;
  const angle = ARM[mood];
  const cheek = mood === 'sad' ? 0.3 : mood === 'nag' ? 0.35 : 0.55;
  const bigEyes = mood === 'wow';
  const eyeRx = bigEyes ? 5.6 : 4.5;
  const eyeRy = bigEyes ? 7.6 : 6;

  return (
    <svg
      width={size}
      height={size * 1.15}
      viewBox="0 0 100 115"
      className={cn('karti shrink-0 overflow-visible', animated && `karti-anim karti-${mood}`, className)}
      role="img"
      aria-label="Karti, das Karteikarten-Maskottchen"
    >
      <ellipse cx="46" cy="113" rx="30" ry="2.6" fill={INK} opacity=".14" />
      <g className="karti-body">
        {/* legs */}
        <rect x="27" y="97" width="9" height="15" rx="4.5" fill={INK} />
        <g className={mood === 'nag' ? 'karti-foot' : undefined}>
          <rect x="53" y="97" width="9" height="15" rx="4.5" fill={INK} />
        </g>

        <Arm side="l" angle={angle} mood={mood} />
        <Arm side="r" angle={mood === 'nag' ? -8 : angle} mood={mood} />

        <rect x="14" y="14" width="74" height="92" rx="14" fill="#F4D03F" stroke={INK} strokeWidth="3" transform="rotate(7 51 60)" />
        <rect x="8" y="8" width="74" height="92" rx="14" fill={PAPER} stroke={INK} strokeWidth="3" />

        {/* brows */}
        {mood === 'sad' && (
          <>
            <path d="M25 36 L38 31" stroke={INK} strokeWidth="3" strokeLinecap="round" />
            <path d="M65 36 L52 31" stroke={INK} strokeWidth="3" strokeLinecap="round" />
          </>
        )}
        {mood === 'nag' && (
          <>
            <path d="M25 31 L38 36" stroke={INK} strokeWidth="3.2" strokeLinecap="round" />
            <path d="M65 31 L52 36" stroke={INK} strokeWidth="3.2" strokeLinecap="round" />
          </>
        )}

        {/* eyes */}
        {mood === 'sleep' ? (
          <>
            <path d="M26 48 q6 5 12 0" stroke={INK} strokeWidth="3.5" fill="none" strokeLinecap="round" />
            <path d="M52 48 q6 5 12 0" stroke={INK} strokeWidth="3.5" fill="none" strokeLinecap="round" />
          </>
        ) : (
          <g className="karti-eyes">
            <ellipse cx="32" cy="46" rx={eyeRx} ry={eyeRy} fill={INK} />
            <ellipse cx="58" cy="46" rx={eyeRx} ry={eyeRy} fill={INK} />
            <circle cx="33.5" cy="43.2" r="1.7" fill={PAPER} />
            <circle cx="59.5" cy="43.2" r="1.7" fill={PAPER} />
            {mood === 'nag' && (
              <>
                <rect x="24" y="37" width="16" height="9" fill={PAPER} />
                <rect x="50" y="37" width="16" height="9" fill={PAPER} />
                <path d="M26 46 h12 M52 46 h12" stroke={INK} strokeWidth="3" strokeLinecap="round" />
              </>
            )}
          </g>
        )}

        <circle cx="21" cy="62" r="5" fill="#EF6B57" opacity={cheek} />
        <circle cx="69" cy="62" r="5" fill="#EF6B57" opacity={cheek} />

        {/* mouth */}
        {mood === 'happy' && (
          <path d="M33 62 q12 18 24 0 z" fill={INK} stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
        )}
        {mood === 'wow' && <ellipse cx="45" cy="70" rx="5.5" ry="7" fill={INK} />}
        {mood === 'sad' && (
          <path d="M36 74 q9 -9 18 0" stroke={INK} strokeWidth="3.5" fill="none" strokeLinecap="round" />
        )}
        {mood === 'nag' && <path d="M37 69 h16" stroke={INK} strokeWidth="3.5" strokeLinecap="round" />}
        {(mood === 'neutral' || mood === 'sleep') && (
          <path d="M36 64 q9 9 18 0" stroke={INK} strokeWidth="3.5" fill="none" strokeLinecap="round" />
        )}

        {mood === 'sad' && (
          <path className="karti-sweat" d="M76 30 q-4 6 0 9 q4 -3 0 -9 z" fill="#7FC4E8" stroke={INK} strokeWidth="1.6" />
        )}
      </g>

      {/* extras outside the body */}
      {(mood === 'happy' || mood === 'wow') && (
        <g fill="#F4D03F" stroke={INK} strokeWidth="1.4">
          <path className="karti-spark" d="M92 12 l2.4 5.6 5.6 2.4 -5.6 2.4 -2.4 5.6 -2.4 -5.6 -5.6 -2.4 5.6 -2.4z" />
          <path className="karti-spark karti-spark-b" d="M2 22 l1.8 4 4 1.8 -4 1.8 -1.8 4 -1.8 -4 -4 -1.8 4 -1.8z" />
        </g>
      )}
      {mood === 'sleep' && (
        <g fontFamily="ui-monospace, monospace" fontWeight="800" fill={INK}>
          <text className="karti-z" x="80" y="20" fontSize="14">Z</text>
          <text className="karti-z karti-z-b" x="90" y="8" fontSize="10">z</text>
        </g>
      )}
      {mood === 'nag' && (
        <text className="karti-bang" x="84" y="26" fontSize="26" fontWeight="900" fill="#EF6B57" stroke={INK} strokeWidth="1.2" fontFamily="system-ui, sans-serif">!</text>
      )}
    </svg>
  );
}
